/**
 * SlushLooping Strategy
 *
 * Slush looping strategy implemented in a separate contract
 * (`alphafi_slush_stsui_sui_loop_pool`). Unlike the other slush strategies it
 * uses its own package, version object and position-cap type, and passes an
 * extra LST info object to deposit/withdraw. Withdrawals are immediate
 * (one-shot) like SlushLending — there is no delayed withdrawal queue.
 *
 * Pool: alphalend-slush-stsui-loop
 */
import { Decimal } from 'decimal.js';
import { BaseStrategy } from './strategy.js';
import { normalizeStructTag } from '@mysten/sui/utils';
import { getConf as getStsuiConf, stSuiExchangeRate } from '@alphafi/stsui-sdk';
import { ALPHALEND_LENDING_PROTOCOL_ID, CLOCK_PACKAGE_ID, GLOBAL_CONFIGS, IMAGE_URLS, SLUSH_LOOP_POSITION_CAP_TYPE, STSUI, SUI_SYSTEM_STATE, } from '../utils/constants.js';
import { planSlushWithdraw, totalSlushXTokens } from '../utils/slushPositions.js';
// The original stSUI/SUI loop pool deployed on the standalone test package. Pools other than
// this one run on the main `alphalend_slush` package, whose autocompound collects stSUI rewards
// itself (no external collect/swap) and which uses a config-driven version + shared cap type.
const OLD_SLUSH_LOOP_POOL_ID = '0x1124c5e7b1fb1f3cfa02cad5934dc27785e083f2b4a49bde3cc41ba66ff9113c';
/**
 * SlushLooping Strategy for the stSUI/SUI loop pool (separate contract).
 */
export class SlushLoopingStrategy extends BaseStrategy {
    poolLabel;
    poolObject;
    receiptObjects = [];
    context;
    constructor(poolLabel, poolObject, context) {
        super();
        this.poolLabel = poolLabel;
        this.poolObject = this.parsePoolObject(poolObject);
        this.context = context;
    }
    getPoolLabel() {
        return this.poolLabel;
    }
    getOtherAmount(_amount, _isAmountA) {
        throw new Error('getOtherAmount is not supported for single-asset SlushLooping strategy');
    }
    updateReceipts(receipts) {
        this.receiptObjects = this.parseReceiptObjects(receipts);
    }
    /**
     * Returns alpha mining data - SlushLooping pools do not support alpha mining
     */
    getAlphaMiningData() {
        return {
            poolId: this.poolLabel.poolId,
            accRewardsPerXtoken: [],
            xTokenSupply: '0',
            receipt: null,
        };
    }
    /**
     * Get the exchange rate for xtoken to underlying token ratio
     * Calculated as tokens_invested / xtoken_supply
     */
    exchangeRate() {
        const tokensInvested = new Decimal(this.poolObject.tokensInvested);
        const xtokenSupply = new Decimal(this.poolObject.xTokenSupply);
        if (xtokenSupply.isZero()) {
            return new Decimal(1);
        }
        return tokensInvested.div(xtokenSupply);
    }
    /**
     * Get comprehensive pool data including TVL and APR information
     */
    async getData() {
        const [alphafi, parent, apr] = await Promise.all([
            this.getTvl(),
            this.getParentTvl(),
            this.context.getAprData(this.poolLabel.poolId),
        ]);
        return {
            poolId: this.poolLabel.poolId,
            strategyType: this.poolLabel.strategyType,
            coinType: this.poolLabel.asset.type,
            poolName: this.poolLabel.poolName,
            apr,
            tvl: {
                alphafi,
                parent,
            },
        };
    }
    /**
     * Calculate total value locked using current asset price
     */
    async getTvl() {
        const coinType = this.poolLabel.asset.type;
        const [price, decimals] = await Promise.all([
            this.context.getCoinPrice(coinType),
            this.context.getCoinDecimals(coinType),
        ]);
        const stsuiAmount = new Decimal(this.poolObject.tokensInvested).div(new Decimal(10).pow(decimals));
        const tokenAmount = await this.toAssetAmount(stsuiAmount);
        const usdValue = tokenAmount.mul(price);
        return { tokenAmount, usdValue };
    }
    /**
     * Calculate parent protocol TVL (Alphalend only)
     */
    async getParentTvl() {
        const protocol = this.poolLabel.parentProtocol;
        if (protocol !== 'Alphalend') {
            throw new Error(`Unsupported parent protocol for SlushLooping: ${protocol}`);
        }
        const [tokenAmount, price] = await Promise.all([
            this.context.getAlphaLendTvl(this.poolLabel.asset.type),
            this.context.getCoinPrice(this.poolLabel.asset.type),
        ]);
        return { tokenAmount, usdValue: tokenAmount.mul(price) };
    }
    /**
     * Calculate user's current pool balance from xToken balance
     * Converts xTokens to underlying tokens via exchange rate
     */
    async getBalance(_userAddress) {
        const totalXTokens = totalSlushXTokens(this.receiptObjects);
        if (totalXTokens === 0n) {
            return { tokenAmount: new Decimal(0), usdValue: new Decimal(0) };
        }
        const xTokens = new Decimal(totalXTokens.toString());
        const [price, exchangeRate, decimals] = await Promise.all([
            this.context.getCoinPrice(this.poolLabel.asset.type),
            Promise.resolve(this.exchangeRate()),
            this.context.getCoinDecimals(this.poolLabel.asset.type),
        ]);
        const stsuiTokens = xTokens.mul(exchangeRate).div(new Decimal(10).pow(decimals));
        const tokens = await this.toAssetAmount(stsuiTokens);
        return { tokenAmount: tokens, usdValue: tokens.mul(price) };
    }
    /**
     * Parse pool object from blockchain response
     */
    parsePoolObject(response) {
        return this.safeParseObject(() => {
            const fields = this.extractFields(response);
            return {
                id: this.getStringField(fields, 'id'),
                xTokenSupply: this.getNestedField(fields, 'x_token_supply.value') ||
                    this.getNestedField(fields, 'xTokenSupply.value'),
                tokensInvested: this.getStringField(fields, 'tokens_invested') ||
                    this.getStringField(fields, 'tokensInvested'),
                positionCount: this.getNestedField(fields, 'positions.size'),
                positionsTableId: this.getNestedField(fields, 'positions.id'),
                feeCollected: this.getStringField(fields, 'fee_collected'),
                paused: this.getBooleanField(fields, 'paused', false),
                investor: {
                    id: this.getStringField(fields.investor, 'id'),
                    marketId: this.getStringField(fields.investor, 'market_id'),
                    borrowMarketId: this.getStringField(fields.investor, 'borrow_market_id'),
                    positionCap: {
                        clientAddress: this.getNestedField(fields, 'investor.alphalend_position_cap.client_address'),
                        id: this.getNestedField(fields, 'investor.alphalend_position_cap.id'),
                        imageUrl: this.getNestedField(fields, 'investor.alphalend_position_cap.image_url'),
                        positionId: this.getNestedField(fields, 'investor.alphalend_position_cap.position_id'),
                    },
                },
            };
        }, 'Failed to parse SlushLooping pool object');
    }
    /**
     * Parse investor object (not applicable as it is embedded in pool)
     */
    parseInvestorObject(_) {
        throw new Error('Investor object embedded in pool for SlushLooping');
    }
    /**
     * Parse parent pool object (not applicable)
     */
    parseParentPoolObject(_) {
        throw new Error('Parent pool object not used for SlushLooping');
    }
    /**
     * Parse receipt objects from blockchain responses
     */
    parseReceiptObjects(responses) {
        return responses.map((response, index) => {
            return this.safeParseObject(() => {
                const fields = this.extractFields(response);
                return {
                    id: this.getStringField(fields, 'id'),
                    positionCapId: this.getStringField(fields, 'position_cap_id'),
                    poolId: this.getStringField(fields, 'pool_id'),
                    coinType: this.getNestedField(fields, 'coin_type.name'),
                    principal: this.getStringField(fields, 'principal'),
                    xTokens: this.getStringField(fields, 'xtokens') ||
                        this.getStringField(fields, 'x_token_balance') ||
                        this.getStringField(fields, 'xTokenBalance'),
                };
            }, `Failed to parse SlushLooping receipt object at index ${index}`);
        });
    }
    createPositionCap(tx) {
        const urlBytes = Array.from(new TextEncoder().encode(IMAGE_URLS.SLUSH_POSITION_CAP));
        const positionCap = tx.moveCall({
            target: `${this.poolLabel.packageId}::alphalend_slush_pool::create_position_cap`,
            arguments: [tx.pure.vector('u8', urlBytes)],
        });
        return positionCap;
    }
    coinAmountToXToken(amount) {
        const exchangeRate = this.exchangeRate();
        return new Decimal(amount).div(exchangeRate).floor().toString();
    }
    /** Whether this pool is the legacy test-package deployment. */
    get isOldPool() {
        return this.poolLabel.poolId === OLD_SLUSH_LOOP_POOL_ID;
    }
    /**
     * Contract balances are always in stSUI. A SUI-asset pool reports SUI amounts (converted via
     * the stSUI exchange rate); a stSUI-asset pool reports stSUI amounts directly.
     */
    get isSuiAsset() {
        return normalizeStructTag(this.poolLabel.asset.type) === normalizeStructTag('0x2::sui::SUI');
    }
    /** stSUI amount -> display amount: scale by the stSUI exchange rate only for a SUI-asset pool. */
    async toAssetAmount(stsuiAmount) {
        if (!this.isSuiAsset) {
            return stsuiAmount;
        }
        const rate = new Decimal(await stSuiExchangeRate(getStsuiConf().LST_INFO, false));
        return stsuiAmount.mul(rate);
    }
    /**
     * Resolve the position cap id to deposit with. The legacy pool has its own cap type; new pools
     * share the main package cap type with the other slush strategies.
     */
    async getDepositPositionCapId(address) {
        const cap = this.isOldPool
            ? await this.context.getSlushPositionCapForDeposit(address, this.poolLabel.poolId, SLUSH_LOOP_POSITION_CAP_TYPE)
            : await this.context.getSlushPositionCapForDeposit(address, this.poolLabel.poolId);
        return cap?.id;
    }
    /**
     * Collect rewards from the position and swap them to SUI (the borrow asset).
     *
     * Uses the rewards-loop pattern (iterate `portfolio.rewardsToClaim`) as in
     * SlushLending, but with the loop contract's move signature
     * (`collect_reward_and_swap_bluefin`, no oracle / slippage args).
     */
    async collectAndSwapRewards(tx) {
        // New pools collect stSUI rewards inside the contract's autocompound, so nothing to do here.
        // Only the legacy pool needs external reward collection + swap.
        if (!this.isOldPool) {
            return;
        }
        const positionId = this.poolObject.investor.positionCap.positionId;
        const alphalendClient = this.context.alphalendClient;
        const portfolio = await alphalendClient.getUserPortfolioFromPosition(positionId);
        const rewards = portfolio?.rewardsToClaim;
        if (!rewards || rewards.length === 0) {
            console.log('no rewards for pool id: ', this.poolLabel.poolId);
            return;
        }
        const [suiCoin] = await this.context.getCoinsBySymbols(['SUI']);
        const rewardsToSwap = rewards.filter((reward) => reward.coinType !== suiCoin.coinType);
        if (rewardsToSwap.length === 0) {
            return;
        }
        // One oracle update for every reward coin, so the PTB carries a single verify + ingest
        // pair instead of one per reward.
        await alphalendClient.updatePrices(tx, [
            ...new Set(rewardsToSwap.map((reward) => reward.coinType)),
            suiCoin.coinType,
        ]);
        for (const reward of rewardsToSwap) {
            // reward -> SUI
            tx.moveCall({
                target: `${this.poolLabel.packageId}::alphafi_slush_stsui_sui_loop_pool::collect_reward_and_swap_bluefin`,
                typeArguments: [reward.coinType, suiCoin.coinType],
                arguments: [
                    tx.object(this.poolLabel.versionId),
                    tx.object(this.poolLabel.poolId),
                    tx.object(ALPHALEND_LENDING_PROTOCOL_ID),
                    tx.object(await this.context.getPoolIdByTypesAndProtocol(reward.coinType, suiCoin.coinType, 'bluefin')),
                    tx.object(GLOBAL_CONFIGS.BLUEFIN),
                    tx.pure.bool(true),
                    tx.pure.bool(true),
                    tx.object(CLOCK_PACKAGE_ID),
                ],
            });
        }
    }
    async deposit(tx, options) {
        const alphalendClient = this.context.alphalendClient;
        const [suiCoin] = await this.context.getCoinsBySymbols(['SUI']);
        const stsuiType = getStsuiConf().STSUI_COIN_TYPE;
        await alphalendClient.updatePrices(tx, [stsuiType, suiCoin.coinType]);
        await this.collectAndSwapRewards(tx);
        // The contract only takes stSUI. Default to the pool's asset type when no coinType is given:
        // SUI -> mint to stSUI; stSUI -> deposit directly; anything else is unsupported.
        const effectiveCoinType = options.coinType ?? this.poolLabel.asset.type;
        let depositCoin;
        if (normalizeStructTag(effectiveCoinType) === normalizeStructTag(suiCoin.coinType)) {
            const suiDepositCoin = this.context.blockchain.getCoinObject(tx, suiCoin.coinType, options.address, BigInt(options.amount));
            [depositCoin] = tx.moveCall({
                target: getStsuiConf().STSUI_LATEST_PACKAGE_ID + '::liquid_staking::mint',
                arguments: [
                    tx.object(getStsuiConf().LST_INFO),
                    tx.object(getStsuiConf().SUI_SYSTEM_STATE_OBJECT_ID),
                    suiDepositCoin,
                ],
                typeArguments: [stsuiType],
            });
        }
        else if (normalizeStructTag(effectiveCoinType) === normalizeStructTag(stsuiType)) {
            depositCoin = this.context.blockchain.getCoinObject(tx, stsuiType, options.address, BigInt(options.amount));
        }
        else {
            throw new Error(`Unsupported coin type for SlushLooping deposit: ${effectiveCoinType}`);
        }
        const existingCapId = await this.getDepositPositionCapId(options.address);
        const target = `${this.poolLabel.packageId}::alphafi_slush_stsui_sui_loop_pool::user_deposit`;
        if (!existingCapId) {
            const positionCap = this.createPositionCap(tx);
            tx.moveCall({
                target,
                arguments: [
                    tx.object(this.poolLabel.versionId),
                    positionCap,
                    tx.object(this.poolLabel.poolId),
                    depositCoin,
                    tx.object(ALPHALEND_LENDING_PROTOCOL_ID),
                    tx.object(STSUI.LST_INFO),
                    tx.object(SUI_SYSTEM_STATE),
                    tx.object(CLOCK_PACKAGE_ID),
                ],
            });
            tx.transferObjects([positionCap], options.address);
        }
        else {
            tx.moveCall({
                target,
                arguments: [
                    tx.object(this.poolLabel.versionId),
                    tx.object(existingCapId),
                    tx.object(this.poolLabel.poolId),
                    depositCoin,
                    tx.object(ALPHALEND_LENDING_PROTOCOL_ID),
                    tx.object(STSUI.LST_INFO),
                    tx.object(SUI_SYSTEM_STATE),
                    tx.object(CLOCK_PACKAGE_ID),
                ],
            });
        }
    }
    async withdraw(tx, options) {
        if (this.receiptObjects.length === 0) {
            throw new Error('No receipt found for withdraw');
        }
        const alphalendClient = this.context.alphalendClient;
        const [suiCoin] = await this.context.getCoinsBySymbols(['SUI']);
        const stsuiType = getStsuiConf().STSUI_COIN_TYPE;
        await alphalendClient.updatePrices(tx, [stsuiType, suiCoin.coinType]);
        const effectiveCoinType = options.coinType ?? this.poolLabel.asset.type;
        if (normalizeStructTag(effectiveCoinType) === normalizeStructTag(suiCoin.coinType)) {
            const rate = new Decimal(await stSuiExchangeRate(getStsuiConf().LST_INFO, false));
            options.amount = new Decimal(options.amount).div(rate).toString();
        }
        // One leg per confirmed position (largest first), each with that position's own cap.
        const legs = planSlushWithdraw(this.receiptObjects, options.withdrawMax ? 'max' : this.coinAmountToXToken(options.amount));
        if (legs.length === 0) {
            throw new Error('Nothing to withdraw');
        }
        await this.collectAndSwapRewards(tx);
        const coins = legs.map((leg) => tx.moveCall({
            target: `${this.poolLabel.packageId}::alphafi_slush_stsui_sui_loop_pool::user_withdraw`,
            arguments: [
                tx.object(this.poolLabel.versionId),
                tx.object(leg.positionCapId),
                tx.object(this.poolLabel.poolId),
                tx.pure.u64(leg.xTokens),
                tx.object(ALPHALEND_LENDING_PROTOCOL_ID),
                tx.object(STSUI.LST_INFO),
                tx.object(SUI_SYSTEM_STATE),
                tx.object(CLOCK_PACKAGE_ID),
            ],
        })[0]);
        const [slushCoin, ...rest] = coins;
        if (rest.length > 0) {
            tx.mergeCoins(slushCoin, rest);
        }
        // The contract always returns stSUI. Default to the pool's asset type when no coinType is
        // given: SUI -> redeem to SUI; stSUI -> return directly; anything else is unsupported.
        if (normalizeStructTag(effectiveCoinType) === normalizeStructTag(suiCoin.coinType)) {
            const [sui] = tx.moveCall({
                target: getStsuiConf().STSUI_LATEST_PACKAGE_ID + '::liquid_staking::redeem',
                arguments: [
                    tx.object(getStsuiConf().LST_INFO),
                    slushCoin,
                    tx.object(getStsuiConf().SUI_SYSTEM_STATE_OBJECT_ID),
                ],
                typeArguments: [stsuiType],
            });
            this.context.blockchain.sendCoinToAddressBalance(tx, suiCoin.coinType, options.address, sui);
        }
        else if (normalizeStructTag(effectiveCoinType) === normalizeStructTag(stsuiType)) {
            this.context.blockchain.sendCoinToAddressBalance(tx, stsuiType, options.address, slushCoin);
        }
        else {
            throw new Error(`Unsupported coin type for SlushLooping withdraw: ${effectiveCoinType}`);
        }
    }
    async claimRewards(_tx, _alphaReceipt) {
        return;
    }
}
//# sourceMappingURL=slushLooping.js.map